/**
 * Agent and report catalogue, plus the shell's zone copy.
 *
 * PLACEHOLDER / ILLUSTRATIVE DATA. Third-party names are retained as text at
 * the PO's instruction, for testing realism. No third-party logos or marks
 * appear anywhere in this application — that is a hard brand rule.
 *
 * Build state:
 *   functional  built end to end here.
 *   mockup      carried the "Mockup" badge in Partnership Net 2.0; per the PO,
 *               deliberately NOT implemented — catalogued as a card only.
 *   unlabelled  carried no badge in 2.0; likewise card only.
 */

import type { AgentModule, ReportModule, SessionUser, ZoneCopy } from '../../contracts';

export const AGENTS: readonly AgentModule[] = [
  {
    kind: 'agent',
    id: 'proi-compass',
    layers: ['valuation'],
    name: 'Partnership Business Case',
    icon: 'compass',
    color: 'commerce',
    buildState: 'functional',
    desc: 'Partnership ROI analysis with Monte Carlo simulation',
    long: 'Comprehensive partnership investment analysis using Monte Carlo simulation to assess risk-adjusted returns. Includes value driver mapping, rights allocation, and scenario planning.',
    moduleRef: 'PNET_PROI-Agent',
    moduleStatus: 'in-use',
    route: '/agent/proi',
  },
  {
    kind: 'agent',
    id: 'rights-allocation',
    layers: ['valuation'],
    name: 'Rights Allocation Builder',
    icon: 'target',
    color: 'brand',
    buildState: 'unlabelled',
    desc: 'Optimize rights distribution across partners',
    long: 'Optimisation engine to allocate partnership rights across multiple partners while maximising total value and minimising conflicts.',
  },
  {
    kind: 'agent',
    id: 'reach-overlap',
    layers: ['scouting'],
    name: 'Reach Overlap',
    icon: 'users',
    color: 'operations',
    buildState: 'unlabelled',
    desc: 'Analyze audience overlap between media channels',
    long: 'Calculate unduplicated reach and audience overlap across multiple media partners to optimise media mix and avoid redundancy.',
  },
  {
    kind: 'agent',
    id: 'budget-recommendation',
    layers: ['valuation'],
    name: 'Budget Recommendation',
    icon: 'scale',
    color: 'audience',
    buildState: 'unlabelled',
    desc: 'Budget allocation optimizer',
    long: 'Budget allocation recommendations based on historical performance, market conditions, and strategic priorities.',
  },
  {
    kind: 'agent',
    id: 'brand-equity',
    layers: ['valuation'],
    name: 'Brand Equity Impact Optimizer',
    icon: 'sparkles',
    color: 'brand',
    buildState: 'unlabelled',
    desc: 'Measure brand equity impact from partnerships',
    long: 'Quantify brand equity lift from partnership activations using the brand tracking methodology and predictive modelling.',
  },
  {
    kind: 'agent',
    id: 'brand-equity-euro',
    layers: ['valuation'],
    name: 'Brand Equity Euro Analysis',
    icon: 'sparkles',
    color: 'operations',
    buildState: 'unlabelled',
    desc: 'Brand valuation and equity measurement in EUR',
    long: 'Brand equity impact analysis with European market focus, measuring brand lift, awareness, and perception changes from partnership investments.',
  },
  {
    kind: 'agent',
    id: 'media-impact',
    layers: ['valuation'],
    name: 'Media Impact Forecast',
    icon: 'radio',
    color: 'audience',
    buildState: 'mockup',
    desc: 'Predict media value and exposure outcomes',
    long: 'Forecasting experience using ERBF methodology to predict media value and exposure across channels. Guided workflow with channel dashboards, asset screening, scenario modelling and P10/P50/P90 forecast intervals.',
  },
  {
    kind: 'agent',
    id: 'mmm-integration',
    layers: ['valuation'],
    name: 'MMM Integration',
    icon: 'database',
    color: 'people',
    buildState: 'functional',
    desc: 'Build MMM-ready sponsorship variable datasets',
    long: 'Builds the sponsorship input variables a marketing-mix model consumes: partnership selection, weekly country-level scope, channel inventory and sources, cost estimate, dataset build, QA and long-table export.',
    moduleRef: 'PNET_MMM-Builder',
    moduleStatus: 'in-use',
    route: '/agent/mmm',
  },
  {
    kind: 'agent',
    id: 'industry-growth',
    layers: ['scouting'],
    name: 'Industry Growth Forecast',
    icon: 'lineChart',
    color: 'extra',
    buildState: 'unlabelled',
    desc: 'Sector-specific growth projections and trends',
    long: 'Growth forecasts for sports partners, leagues, and categories with scenario-based projections.',
  },
  {
    kind: 'agent',
    id: 'portfolio-reach',
    layers: ['scouting'],
    name: 'Portfolio Reach',
    icon: 'network',
    color: 'brand',
    buildState: 'unlabelled',
    desc: 'Aggregate reach across the partnership portfolio',
    long: 'Analyse total unduplicated reach and audience delivery across the entire partnership portfolio to optimise media value and minimise overlap.',
  },
  {
    kind: 'agent',
    id: 'strategic-relevance',
    release: 'beta-waitlist',
    layers: ['scouting'],
    name: 'Strategic Relevance Analysis',
    icon: 'layers',
    color: 'operations',
    buildState: 'functional',
    desc: 'Assess strategic fit and brand alignment',
    long: 'Scores a partnership across Brand, Business, People, Planet and Strategic Alignment, applies the bonus/malus discount factor, and places it on the portfolio decision matrix.',
    moduleRef: 'strategic-relevance',
    moduleStatus: 'in-use',
    route: '/agent/sr',
    deployment: 'pending',
  },
  {
    kind: 'agent',
    id: 'pricing-agent',
    release: 'beta-waitlist',
    layers: ['valuation'],
    name: 'Pricing Agent',
    icon: 'dollar',
    color: 'people',
    buildState: 'functional',
    desc: 'What is this partnership worth, and where does the fee sit?',
    long: 'Top-down across categories from a short question set, or bottom-up at rights level with the full method library. Produces a defensible price range with the workings shown — ready to use in a negotiation or a governance pack.',
    moduleRef: 'PNET_Pricing-Agent',
    moduleStatus: 'in-use',
    route: '/agent/pricing',
    deployment: 'pending',
  },
  {
    kind: 'agent',
    id: 'interbrand-rank-impact',
    layers: ['valuation'],
    name: 'Interbrand Rank Impact',
    icon: 'trophy',
    color: 'extra',
    buildState: 'mockup',
    desc: 'Measure partnership impact on brand strength',
    long: 'Assess how partnerships with rights holders contribute to the ten brand-strength dimensions and estimate projected rank improvements.',
  },
  {
    kind: 'agent',
    id: 'product-launch-analysis',
    layers: ['valuation'],
    name: 'Product Launch Analysis',
    icon: 'pkg',
    color: 'people',
    buildState: 'unlabelled',
    desc: 'Evaluate product launches through partnership channels',
    long: 'Analyse the effectiveness of product launches leveraged through partnership activations, including reach amplification, sales impact, and market penetration.',
  },
  {
    kind: 'agent',
    id: 'okr-proposal',
    layers: ['platform'],
    name: 'OKR Proposal',
    icon: 'checkCircle',
    color: 'commerce',
    buildState: 'mockup',
    desc: 'Create partnership OKRs from analysis or manual setup',
    long: 'Define partnership activation OKRs based on existing business-case value drivers or create them manually, with a chosen time horizon and measurable key results.',
  },
  {
    kind: 'agent',
    id: 'contract-proposal',
    layers: ['valuation'],
    name: 'Contract Proposal',
    icon: 'fileSign',
    color: 'brand',
    buildState: 'mockup',
    desc: 'Contract optimization and collaboration',
    long: 'Create, optimise and refine partnership contracts with a six-phase workflow (Setup, Analysis, Recommendations, Legal Refinement, Sharing, Signature), template management and collaborative review.',
  },
  {
    kind: 'agent',
    id: 'data-qa-agent',
    layers: ['data-brokerage'],
    name: 'Data Quality Assurance',
    icon: 'shield',
    color: 'audience',
    buildState: 'functional',
    desc: 'Checks a delivered dataset, and a delivery file at the handover gate',
    long: 'One rule engine in its two positions \u2014 on a dataset before anyone builds on it, and on a delivery file at the handover gate. Runs across integrity, completeness, plausibility, provenance and model-fitness, names offending rows, and says plainly what it could not check. A check that could not run is reported as not run, never as a pass.',
    moduleRef: 'PNET_DataQA-Assessment',
    moduleStatus: 'prototype',
    route: '/agent/dataqa',
    deployment: 'pending',
  },
  {
    kind: 'agent',
    id: 'data-qa-measurement',
    layers: ['data-brokerage'],
    name: 'Data Confidence Score',
    icon: 'barChart',
    color: 'extra',
    buildState: 'functional',
    desc: 'How much a figure can be trusted for the decision it has to carry',
    long: 'Scores the evidence behind every figure — coverage, depth and provenance — judged against what this decision needs, and tells absence apart from weakness. It measures the evidence, not the sponsorship: a well-measured weak asset scores high. A low score is a roadmap, not a verdict.',
    moduleRef: 'dqs-scorecard',
    moduleStatus: 'in-use',
    route: '/agent/dqsm',
    deployment: 'pending',
  },
  {
    kind: 'agent',
    id: 'data-inventory',
    layers: ['data-brokerage'],
    name: 'Data Inventory',
    icon: 'library',
    color: 'people',
    buildState: 'planned',
    desc: 'What data exists, against what the decision needs',
    long: 'Maps what data exists across partner, channel and period, and measures it against what the decision actually needs — so an absence is visible as an absence rather than showing up later as a zero.',
  },
  {
    kind: 'agent',
    id: 'data-recommendation-agent',
    deployment: 'pending',
    layers: ['data-brokerage'],
    name: 'Data Recommendation Agent',
    icon: 'list',
    color: 'brand',
    buildState: 'planned',
    // Not vapourware and not built either: the ranking-and-pricing of fixes
    // runs today inside the Data Confidence Score tool. Splitting it out is the
    // migration both handovers describe (DBS v1.2 §5 build note; DRA v1.0 §9),
    // so it is catalogued here rather than left invisible.
    long: 'Reads the findings from the Data Inventory, Data Quality Assurance and the Data Confidence Score, sets a target for each partner and channel based on what the decision needs, prices the fix and ranks the work against a real budget. Names the capability, never the vendor. Every cost is an estimate until a vendor has quoted it, and everything left unfunded is shown with its reason.',
    desc: 'Turns the findings into a costed sourcing strategy',
  },
  {
    kind: 'agent',
    id: 'portfolio-optimization',
    layers: ['valuation'],
    release: 'beta-waitlist',
    name: 'Portfolio Optimization',
    icon: 'scale',
    color: 'commerce',
    buildState: 'planned',
    desc: 'Where the next euro of partnership budget earns most',
    long: 'Reads the portfolio as one allocation problem rather than a list of deals \u2014 what each partnership returns, what it costs, what it crowds out \u2014 and proposes the reallocation, with the assumptions it rests on stated beside it.',
  },
  {
    kind: 'agent',
    id: 'competition-insurance-simulator',
    layers: ['valuation'],
    segment: 'rights-holder',
    name: 'Competition Insurance Simulator',
    icon: 'shield',
    color: 'operations',
    buildState: 'mockup',
    desc: 'What a cancelled or curtailed competition costs, and what cover is worth',
    long: 'Models the revenue at risk when a competition is cancelled, shortened or played behind closed doors, and prices what transferring that risk is worth against carrying it.',
  },
  {
    kind: 'agent',
    id: 'equity-value',
    layers: ['valuation'],
    segment: 'rights-holder',
    name: 'Equity Value',
    icon: 'trendUp',
    color: 'brand',
    buildState: 'mockup',
    desc: 'What the rights portfolio contributes to enterprise value',
    long: 'Connects commercial performance to the valuation of the organisation itself \u2014 how the rights portfolio, its contract lengths and its renewal risk read to an investor or an acquirer rather than to a sponsorship team.',
  },
];

export const REPORTS: readonly ReportModule[] = [
  {
    kind: 'report',
    id: 'partnership-due-diligence',
    layers: ['valuation'],
    name: 'Partnership Due Diligence',
    icon: 'fileCheck',
    color: 'commerce',
    desc: 'Comprehensive partnership evaluation report',
    long: 'Consolidates business-case, reach, strategic relevance, SWOT and competitive alternatives into an executive one-pager.',
    status: 'soon',
  },
  {
    kind: 'report',
    id: 'fair-market-value-report',
    layers: ['valuation'],
    name: 'Fair Market Value',
    icon: 'dollar',
    color: 'people',
    desc: 'Benchmark-driven rights valuation report',
    long: 'Fair market value assessment using the benchmarking database, comparable transaction analysis, and market pricing trends.',
    status: 'soon',
  },
  {
    kind: 'report',
    id: 'head-to-head-comparison',
    layers: ['scouting', 'valuation'],
    name: 'Head to Head Comparison',
    icon: 'branch',
    color: 'commerce',
    desc: 'Compare partnership opportunities side-by-side',
    long: 'Side-by-side comparison of shortlisted partnership opportunities across strategic fit, price range, reach and risk, with the deciding differences called out rather than left for the reader to find.',
    status: 'soon',
  },
  {
    kind: 'report',
    id: 'activation-report',
    release: 'beta-waitlist',
    layers: ['valuation'],
    name: 'Activation Impact Report',
    icon: 'file',
    color: 'brand',
    desc: 'Create client-ready sponsorship reports with platform data',
    long: 'Generate end-of-season or mid-season sponsorship reports from platform data plus manual quality inputs. Guided wizard, modular section builder, deep-dive modules and collaboration tools.',
    status: 'live',
    route: '/report/activation',
    deployment: 'pending',
  },
  {
    kind: 'report',
    id: 'dqs-assessment',
    release: 'beta-waitlist',
    layers: ['data-brokerage'],
    // Identifier kept: identifiers are permanent, display names are mutable.
    name: 'Data Due Diligence',
    icon: 'shield',
    color: 'audience',
    desc: 'The pillar\u2019s aggregated verdict on the data behind a decision',
    long: 'Aggregates whichever Data Brokerage entities ran \u2014 inventory, quality assurance, confidence scoring \u2014 into one client deliverable: what the data supports, what it does not, and what could not be checked. The sibling of the Partnership Due Diligence.',
    status: 'live',
    route: '/report/dqs',
    deployment: 'pending',
  },
];

export const CATALOGUE = [...AGENTS, ...REPORTS];

export const SESSION_USER: SessionUser = {
  displayName: 'Paulo Pinto',
  initials: 'PP',
  role: 'Product Owner · Partnership Net',
  environment: 'demo · eu-central-1 (Frankfurt)',
};

export const ZONES: readonly ZoneCopy[] = [
  {
    id: 'game',
    layers: ['kaas'],
    eyebrow: 'Showtime',
    title: 'The Game',
    tagline: 'One whistle. One shot. Make it iconic.',
    body: 'This is where you perform. Every hour in the gym, every play drawn up in the lockerroom — it all funnels into this moment. Every metric, every story, every signal is at your fingertips.',
    stats: [
      ['Audience', 'Boardroom', 'crown'],
      ['State', 'Game time', 'flame'],
      ['Energy', 'Iconic', 'star'],
    ],
    cta: 'Step on the pitch',
    href: '/game',
    icon: 'goal',
    pillar: 'extra',
  },
  {
    id: 'lockerroom',
    eyebrow: 'Strategy huddle',
    title: 'The Lockerroom',
    tagline: 'Set the lineup. Sharpen the playbook.',
    body: 'This is where strategy gets set. You pick your best players, fine-tune the gameplan on last-minute intel, and stack a deep bench of resources ready to be pulled the second the stage calls.',
    stats: [
      ['Squad', '23 agents', 'activity'],
      ['Playbook', 'Live', 'goal'],
      ['State', 'Pre-game', 'clock'],
    ],
    cta: 'Enter the lockerroom',
    href: '/lockerroom',
    icon: 'clipboard',
    pillar: 'commerce',
  },
  {
    id: 'front-office',
    layers: ['marketplace'],
    eyebrow: 'Scout · Sign · Support',
    title: 'The Front Office',
    tagline: 'Nobody wins alone. Build the network that backs you.',
    body: 'This is where you scout, select and trade for the network of support around you. Find everything you need, when you need it, on your conditions — legal, payments, paperwork, all handled.',
    stats: [
      ['Network', 'Experts', 'users'],
      ['Service', 'One-stop', 'briefcase'],
      ['Admin', 'Handled', 'scroll'],
    ],
    cta: 'Walk into the front office',
    href: '/front-office',
    icon: 'handshake',
    pillar: 'brand',
    note: 'The Marketplace will be implemented here.',
  },
  {
    id: 'gym',
    layers: ['platform'],
    eyebrow: 'The grind',
    title: 'The Gym',
    tagline: 'Where winners are built — long before kickoff.',
    body: 'This is the operating system. 80% of the time you are not on stage and not in the lockerroom — you are here, getting better than everyone else and, more importantly, better than your past self.',
    stats: [
      ['Platform', 'Partnership Net', 'activity'],
      ['Discipline', 'Daily reps', 'medal'],
      ['State', 'Training', 'dumbbell'],
    ],
    cta: 'Hit the gym',
    href: '/gym',
    icon: 'dumbbell',
    pillar: 'people',
    note: 'This is the existing Partnership Net platform.',
  },
];
